(ns minorodata.components.transform-queue.core
  (:require ["copy-to-clipboard" :as copy]
            ["date-fns" :refer [isBefore parseISO]]
            ["lucide-react" :refer [CheckCircle Copy Ellipsis RefreshCcw]]
            ["react" :as react]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.utils :refer [format-execution-time]]
            [minorodata.components.spinner.core :refer [spinner-lucide]]
            [re-posh.core :as re-posh]
            [reitit.frontend.easy :as rt.easy]
            [ui.button :refer [button]]
            [ui.date-time :refer [date-tooltip]]
            [ui.page :refer [page-content page-header page-root page-title]]
            [ui.popover :as popover]
            [ui.table :refer [table tbody td th thead tr]]
            [ui.typography :as typography]
            [ui.fields :refer [checkbox]]
            [ui.utils :refer [clsx]]
            [ui.tooltip :as tooltip]))

(defn fetch-queue []
  (dispatch [:transform.api/fetch-transform-queue]))

(defn use-transform-queue []
  (react/useEffect
   (fn []
     (fetch-queue)
     js/undefined)
   #js [])

  {:data           @(re-posh/subscribe [:transform-queue/prop :transform-queue/data])
   :loading        @(re-posh/subscribe [:mold-http/loading? :transform.api/fetch-transform-queue])})

(defn running-first [tasks]
  (sort-by #(not (:is-executing %)) tasks))

(def status-names
  {"running"   "Running"
   "completed" "Completed"
   "failed"    "Failed"
   "pending"   "Pending"
   "skipped"   "Skipped"})

(defn loader [{:keys [loading]}]
  [:div {:class (clsx "absolute inset-0 bg-black/10 z-100 flex items-center transition-opacity justify-center opacity-0 pointer-events-none"
                      {"opacity-100 pointer-events-auto" loading})}
   [spinner-lucide]])

(defn table-head []
  [thead
   [tr
    (for [name ["Transform name" "Started at" "Finished at" "Duration" "Status" ""]]
      ^{:key name}
      [th name])]])

(defn row-highlight [task]
  (if (:is-executing task)
    "bg-kleene-text-warning/20"
    (case (:status task)
      "running"   "bg-kleene-text-warning/20"
      "failed"    "bg-kleene-text-error/20"
      "completed" "bg-kleene-text-success/20"
      nil)))

(defn sort-by-date-desc [d1 d2]
  (isBefore (parseISO d2) (parseISO d1)))

(defn job-row [{:keys [task]}]
  (let [{:keys [id job-id started-at finished-at created-at group-id transform-id name status is-executing]} task]
    [tr {:class (row-highlight task)}
     [td {:width "40%"} name]
     [td (when (or is-executing (not= status "pending"))
           [date-tooltip {:date started-at}])]
     [td [date-tooltip {:date finished-at}]]
     [td (when (or is-executing (not= status "pending"))
           (format-execution-time {:started-at  started-at
                                   :finished-at (or finished-at (js/Date.))}))]
     [td (if is-executing
           "Running"
           (get status-names status status))]
     [td {:width "56"}
      [popover/popover-root
       [popover/popover-trigger
        [:div
         [button {:size    :icon
                  :variant :empty-transparent
                  :icon    Ellipsis
                  :component :div}]]]
       [popover/popover-content
        [:div {:class "flex flex-col gap-1 items-start text-xs"}
         [:div
          [typography/h5 "Job id"]
          [:div {:class "flex gap-2 items-center"}
           [:span job-id]
           [button {:size :icon-xs :icon Copy :variant :empty :on-click #(copy job-id)}]]]
         [:div
          [typography/h5 "Task id"]
          [:div {:class "flex gap-2 items-center"}
           [:span id]
           [button {:size :icon-xs :icon Copy :variant :empty :on-click #(copy id)}]]]
         [:div
          [typography/h5 "Group id"]
          [:div {:class "flex gap-2 items-center"}
           [:span group-id]
           [button {:size :icon-xs :icon Copy :variant :empty :on-click #(copy group-id)}]]]
         [:div
          [typography/h5 "Transform id"]
          [:div {:class "flex gap-2 items-center"}
           [:span transform-id]
           [button {:size :icon-xs :icon Copy :variant :empty :on-click #(copy transform-id)}]]]
         [:div
          [typography/h5 "Created at "]
          [:span [date-tooltip {:date created-at}]]]
         [:div {:class "flex gap-1 mt-2"}
          [button {:size :small
                   :variant :empty
                   :href (rt.easy/href :log {} {:source "job-manager"
                                                :term   name})}
           "View logs"]
          [button {:size :small
                   :variant :empty
                   :href (rt.easy/href :transform-details {:group-id     group-id
                                                           :transform-id transform-id})}
           "View transform"]]]]]]]))

(defn job-table [{:keys [tasks]}]
  (let [{:keys [done undone]} (group-by #(if (or (:is-executing %) (= (:status %) "pending"))
                                           :undone :done)
                                        tasks)
        sorted-tasks     (concat (sort-by :created-at sort-by-date-desc undone)
                                 (sort-by :finished-at sort-by-date-desc done))
        overflow? (> (count tasks) 4)
        more (- (count tasks) 4)
        finished? (every? #(not= (:status %) "pending") tasks)
        [expanded? set-expanded] (react/useState false)]

    [:div
     [:header {:class "flex justify-between items-center mb-2"}
      [typography/h3 {:class "flex gap-2 items-center h-8"}
       [date-tooltip {:date (:created-at (first tasks))}]
       (when finished?
         [tooltip/tooltip {:content "Every task within the job has completed"}
          [:> CheckCircle {:size 20 :class "text-kleene-text-success"}]])]
      (when (and (not expanded?) overflow?)
        [button {:size     :small
                 :variant  :empty
                 :on-click #(set-expanded true)}
         (str "Show " more " more")])

      (when (and expanded? overflow?)
        [button {:size     :small
                 :variant  :empty
                 :on-click #(set-expanded false)}
         "Show less"])]
     [:div {:class "rounded-xs border border-kleene-layout-border bg-kleene-background overflow-hidden"}
      [table {:class "-mb-px"}
       [table-head]
       [tbody
        (for [{:keys [id] :as task} (cond->> sorted-tasks
                                      (not expanded?) (take 4))]
          ^{:key id}
          [job-row {:task task}])]]]]))

(defn queues-list [{:keys [data]}]
  (let [jobs (->> data
                  (group-by :job-id)
                  (sort-by (comp :created-at first second) sort-by-date-desc)
                  (map (fn [[k v]] [k (running-first v)])))]
    [:<>
     [:div {:class "flex flex-col gap-8"}
      (when (seq jobs)
        [:div {:class "flex flex-col gap-4"}
         (for [[id tasks] jobs]
           ^{:key id}
           [:f> job-table {:tasks tasks}])])]]))

(defn queues [{:keys [hide-completed?]}]
  (let [{:keys [data loading]} (use-transform-queue)
        data (if hide-completed?
               (filter #(-> % :status (not= "completed"))
                       data)
               data)
        empty (and (empty? data) loading)]
    [:<>
     (if empty
       [typography/h2 "No jobs in queue"]
       [queues-list {:data data}])
     [loader {:loading loading}]]))

(defn page' []
  (let [[hide-completed? set-hide-completed] (react/useState false)]
    [page-root
     [page-header {:content-props {:class "flex items-center justify-between"}}
      [page-title "Transform queue"]
      [:div {:class "flex flex-row gap-2 items-center"}
       [checkbox {:label     "Hide completed"
                  :checked   hide-completed?
                  :on-change set-hide-completed
                  :classes   {:root "px-3 w-full"}}]
       [button {:variant  :empty
                :icon     RefreshCcw
                :on-click fetch-queue}
        "Refresh"]]]
     [page-content {:class "pb-kai"}
      [:f> queues {:hide-completed? hide-completed?}]]]))

(defn page []
  [:f> page'])
